import request from '@utils/request'
import xhttp from '@utils/xhttp'
import { mergeMeta } from '@mypaas/app-render-engine/core/sdk/plugin/web/shared/mergeMeta'

const isDev = process.env.NODE_ENV === 'development'

// 获取预览元数据
export function getPreviewAppMeta(params) {
    // 接口中包含了扩展函数元数据及标准应用元数据
    return request.get('/api_v3/studio/metadata/assembled/preview', params).then(({ ext, standard }) => {
        if (!standard) throw new Error('应用元数据加载失败')
        if (!ext) return standard
        return mergeMeta(standard, ext)
    })
}

// 应用详情
export function getAppDetail({ appId, workspaceId }) {
    return request.get(`/api_v3/studio/app/${appId}/workspace/${workspaceId}`).then(d => {
        if (!isDev) return d

        const { VUE_APP_TARGET: appTarget } = process.env
        let env = 'local'
        if (appTarget) {
            const result = /studio-?(.*?)\./.exec(appTarget)
            if (result) {
                env = result[1] || 'prod'
            }
        }

        return {
            ...d,
            previewUrl: `http://${window.location.hostname}:2234`,
            studioUrl: `http://${window.location.hostname}:1234`,
            targetEnv: env,
            ossDomain: env === 'prod' ? 'app-resource-prod' : 'app-resource-internal',
        }
    })
}

export function getValidPurview({ appId }) {
    return request.get(`/api/tenant/${appId}/get-valid-purview`)
}
export function saveAppSetting(params) {
    return request.post('/api_v3/studio/theme', params)
}
export function setAppSetting(params) {
    return request.post('/api/admin/webapp/global-setting', params)
}

// 组件
export function getComponents() {
    return request.get('/api_v3/studio/workspace-component/componentList')
}

// 页面
export function getPages(params = {}) {
    return request.get('/api_v3/studio/page', { params }).then(res => {
        const { groupList, pageList } = res
        return [
            ...groupList.map(group => ({
                ...group,
                id: group.name,
            })),
            ...pageList,
        ]
    })
}

export function createPage(params) {
    return request.post('/api_v3/studio/page', params)
}

export function createPageByTemplate(params) {
    return request.post('/api_v3/studio/page/byTemplate', params)
}

export function getPageTemplate(params) {
    return request.get('/api_v3/studio/page-template', { params })
}

export function updatePage({ id, ...params }) {
    return request.patch(`/api_v3/studio/page/${id}`, params)
}

export function copyPage({ appId, workspaceId, ...params }) {
    const { id: configId, ...rest } = params
    return request.post(
        `/api_v3/studio/page/${configId}/copy`,
        rest
    )
}

export function deletePage({ pageId, isRestore, ...params }) {
    if (isRestore) {
        return request.post(`/api_v3/studio/page/${pageId}/rollback`, { data: params })
    }
    return request.delete(`/api_v3/studio/page/${pageId}`, { data: params })
}

export function movePage({ pageId, ...params }) {
    return request.post(`/api_v3/studio/page/${pageId}/move`, params)
}

export function updateStatus({ pageId, ...params }) {
    return request.post(`/api/page/${pageId}/update-status`, params)
}

export function setLogin({ pageId, ...params }) {
    return request.post(`/api_v3/studio/page/${pageId}/login`, params)
}

export function unsetLogin({ pageId, ...params }) {
    return request.delete(`/api_v3/studio/page/${pageId}/login`, { data: params })
}

export function setHome(pageId) {
    return request.post(`/api_v3/studio/page/${pageId}/home-page`)
}

export function savePageMeta({ pageId, ...params }) {
    return request.put(`/api_v3/studio/page/${pageId}`, params)
}

export function saveNavigationMeta(params) {
    return request.post('/api_v3/studio/navigations/save', params)
}

export function updatePageMeta(payload) {
    return request.post('/api/admin/webapp-page/update-metadata', payload)
}

export function getSystemPages(webappId) {
    return request.get(`/api/admin/webapp-syspage/list/${webappId}`)
}

export function alterPageLocker(payload) {
    const { type, lockInfo = {} } = payload
    const { pageId, pageName } = lockInfo
    if (type === 1) {
        return request.post(`/api_v3/studio/page-lock/${pageId}`, { pageName })
    }
    if (type === 2) {
        return request.post(`/api_v3/studio/page-lock/${pageId}/delete`, { pageName })
    }
    if (type === 3) {
        return request.put(`/api_v3/studio/page-lock/${pageId}/forced`, { pageName })
    }

    return Promise.reject((new Error('未获取到锁操作 type')).message)
}

export function updatePageExps({
    webappId,
    workspaceId,
    pageId,
    ...params
}) {
    return request.post(
        `/api/page/${webappId}/workspace/${workspaceId}/update-page-expression/${pageId}`,
        params
    )
}

export function getAssignedPage(params) {
    const { pageIds } = params
    return request.get(`/api_v3/studio/page/${pageIds[0]}`).then(page => [page])
}

export function getNavigations() {
    return request.get('/api_v3/studio/navigations')
}

// 文件夹
export function createFolder(params) {
    return request.post('/api/folder', params)
}

export function deleteFolder({ pageId }) {
    return request.delete(`/api_v3/studio/page-group/${pageId}`)
}

export function copyFolder({ appId, workspaceId, ...params }) {
    const { id: configId, ...rest } = params
    return request.post(
        `/api/folder/app/${appId}/workspace/${workspaceId}/copy-folder`,
        { configId, ...rest }
    )
}

export function updateFolder({ id, ...params }) {
    return request.put(`/api/folder/${id}`, params)
}

// 配置
export function getConfList() {
    return request.get('/api_v3/studio/conf')
}

export function saveConf({ roleId, ...params }) {
    return request.post(`/api/conf/${roleId}/save`, params)
}

export function getSystemConfList(webappId) {
    return request.get(`/api/admin/webapp-sysconf/list/${webappId}`)
}

export function getUserList(webappId) {
    return request.get(`/api/admin/tenant/user-group-list/${webappId}`)
}

export function addConf(params) {
    return request.post('/api/admin/webapp-conf/create', params)
}

export function saveConfV2(params) {
    return request.post('/api/admin/webapp-conf/save/v2', params)
}

export function updateConf(params) {
    return request.post('/api/admin/webapp-conf/update', params)
}

export function resetConf(params) {
    const { confId, ...other } = params
    return request.post(`/api/admin/webapp-conf/reset/${confId}`, other)
}

export function deleteConf(webappId) {
    return request.delete(`/api/admin/webapp-conf/delete/${webappId}`)
}

export function getMetadataStatus() {
    return request.get('/api/conf/metadata/status')
}

export function getBusinessEnvDetail(payload) {
    const { appId, envId } = payload
    return request.get(`/api/info/${appId}/business-env/${envId}`)
}

// 获取集成应用列表
export function getAppList(webAppId) {
    return request.get(`/api/admin/${webAppId}/app`)
}

// 获取当前配置的小程序构建状态
export function getConfBuildStatus(confId) {
    return request.get(`/api/admin/webapp-conf/${confId}/build-status`)
}

// 获取当前配置的小程序构建日志
export function getConfBuildLog({ confId, ...params }) {
    return request.get(`/api/admin/webapp-conf/${confId}/build-log`, { params })
}

// 构建当前配置的小程序
export function startConfBuild(confId) {
    return request.post(`/api/admin/webapp-conf/${confId}/build-mini-program`)
}

// 查询已使用的小程序id
export function queryWxappId(confId, options) {
    return request.get(`/api/admin/miniupload/miniid/${confId}`, options)
}
// 小程序一键上传
export function startWeappUpload(payload) {
    return request.post('/api/admin/miniupload', payload)
}

// 查询上次小程序一键上传任务id
export function queryLastUpload(confId, options) {
    return request.get(`/api/admin/miniupload/lasttask/${confId}`, {
        headers: { 'Cache-Control': 'no-cache' },
        ...options,
    })
}

// 小程序一键上传状态查询
export function queryWeappUpload({ taskid }) {
    return request.get(`/api/admin/miniupload/${taskid}`, {
        headers: { 'Cache-Control': 'no-cache' },
    })
}

// refresh，如:租户操作时，开发者元数据更新
export function designerRefresh(next) {
    return request.get('/api/admin/webapp/refresh', { params: { next } })
}

// 获取模版列表
export function getTemplateList() {
    return request.get('/api/admin/webapp/template/list')
}

// 模版上架
export function setTemplateStatus(params = {}) {
    return request.post('/api/admin/webapp/template/status', params)
}

// 模版启用
export function useTemplate(params) {
    return request.post('/api/admin/webapp/template/enable', params)
}

// 模版截图上传
export function updateTemplateCover(params) {
    return request.post('/api/admin/webapp/template/pic-update', params)
}

// 获取 oss 配置
export function getAliOssSts(params) {
    console.log('api')
    return request.get('/api_v3/studio/storages/security-token', { params, isCancel: false })
}

export function addGalleryImage(params) {
    return request.post('/api/image-library/add', params)
}

// 获取图片列表
export function getGalleryImage(params) {
    return request.get('/api/image-library', { params })
}

// 删除图片（图片列表）
export function deleteGalleryImage(params) {
    return request.delete('/api/image-library/batch-delete-image', { params })
}

// 更新图片（图片列表）
export function updateGalleryImage({ id, ...params }) {
    return request.put(`/api/image-library/${id}`, params)
}

// 移动图片文件夹
export function moveGalleryImage(params) {
    return request.put('/api/image-library/batch-update-image', params)
}

// 获取图片库分组
export function getGalleryGroup({ ...params }) {
    return request.get('/api/image-library/image-folder', { params })
}

// 创建图片库分组
export function createGalleryGroup({ ...params }) {
    return request.post('/api/image-library/image-folder', params)
}

// 删除图片库分组
export function deleteGalleryGroup(params) {
    const { id } = params
    delete params.id
    return request.delete(`/api/image-library/image-folder/${id}`, { params })
}

// 移动图片库分组内容至另一分组
export function moveGalleryGroup(params) {
    return request.put('/api/image-library/move-image-folder', params)
}

// 更新图片库分组
export function updateGalleryGroup({ id, ...params }) {
    return request.put(`/api/image-library/image-folder/${id}`, params)
}

/**
 * 获取应用的 iconfont 信息
 * @param {string} webappId
 */
export function getIconInfo(params) {
    return request.get('/api/admin/icon-library/icons', { params })
}

//全量发布接口增加
export function updateConfAll(params) {
    return request.put('/api/admin/webapp-conf/update-all-with-conf', params)
}
export function updateConfAllV2(params) {
    return request.put('/api/admin/webapp-conf/update-all-with-conf/v2', params)
}
export function updatePageMetaAll(payload) {
    return request.put('/api/admin/webapp-page/update-all-with-page', payload)
}

// 标记新用户
export function uriAccessed({ uri }) {
    return request.post('/api/admin/user-access', { uri })
}

export function uriAccess({ uri }) {
    return request.get(
        '/api/admin/user-access',
        { params: { uri } },
        { headers: { 'Cache-Control': 'no-cache' } }
    )
}
export function getHistory(webAppId) {
    return request.get(`/api/admin/webapp/${webAppId}/publish-histories`)
}
//恢复历史记录
export function postRecover({ webAppId, historyId }) {
    return request.post(
        `/api/admin/webapp/${webAppId}/publish-histories/${historyId}/restore`
    )
}
//获取历史详情
export function getHistoryDetail(params) {
    const query = Object.keys(params)
        .filter(key => params[key])
        .reduce((a, b) => `${a}${b}=${params[b]}&`, '?')
    return request.get(`/api/admin/webapp-log${query}`, params)
}

export function getExpressionList(webappId, objectId) {
    return request.get(
        `/api/admin/webapp/${webappId}/objects/${objectId}/expressions`
    )
}

export function getExpression(webappId, objectId, expressionId) {
    return request.get(
        `/api/admin/webapp/${webappId}/objects/${objectId}/expressions/${expressionId}`
    )
}

export function createExpression(webappId, objectId, expression) {
    return request.post(
        `/api/admin/webapp/${webappId}/objects/${objectId}/expressions`,
        expression
    )
}

export function deleteExpression(webappId, objectId, expressionId) {
    return request.delete(
        `/api/admin/webapp/${webappId}/objects/${objectId}/expressions/${expressionId}`
    )
}

export function updateExpression(webappId, objectId, expression) {
    const { id: expressionId, ...data } = expression
    return request.put(
        `/api/admin/webapp/${webappId}/objects/${objectId}/expressions/${expressionId}`,
        data
    )
}

// 扩展代码
export function getFunctionsList() {
    return request.get('/api_v3/studio/extended-function')
}

export function addFunction(webappId, webappCode, params) {
    return request.post(
        `/api/extended-function/${webappId}/code/${webappCode}`,
        params
    )
}

export function batchAddFunction(webappId, webappCode, params) {
    return request.post(
        `/api/extended-function/${webappId}/code/${webappCode}/batch`,
        params
    )
}

export function editFunction(webappId, webappCode, id, params) {
    return request.put(
        `/api/extended-function/${webappId}/code/${webappCode}/function/${id}`,
        params
    )
}

export function saveFunction(id, content) {
    return request.post(`/api/admin/extension-code/${id}`, {
        content,
    })
}

export function deleteFunction(webappId, webappCode, id) {
    return request.delete(
        `/api/extended-function/${webappId}/code/${webappCode}/function/${id}`
    )
}

export function batchDeleteFunction(webappId, webappCode, params) {
    return request.post(
        `/api/extended-function/${webappId}/code/${webappCode}/function-batch`,
        { data: params }
    )
}

export function getUsedFunctionsList(webappId, params) {
    return request.post(
        `/api/extended-function/${webappId}/fetch-metadata/extended-function`,
        params
    )
}

export function buildFunction(params) {
    const {
        appId, appCode, ...payload
    } = params
    return request.put(
        `/api/extended-function/${appId}/code/${appCode}/build-extended-function`,
        payload
    )
}

export function batchBuildFunction(params) {
    const {
        appId, appCode, ...payload
    } = params
    return request.put(
        `/api/extended-function/${appId}/code/${appCode}/build-extended-function/batch`,
        payload
    )
}

// 校验函数
export function getValidatorFunctionsList(appId, appCode, params) {
    return request.post(
        `/api/extended-function/batch/${appId}/code/${appCode}`,
        params
    )
}

// 发布
export function addVersion(params) {
    return request.post('/api_v3/studio/app-version', params)
}

export function publishMetadata(params) {
    return request.post('/api/publish/metadata', params)
}

export function getWorkspaceEnv() {
    return request.get('/api_v3/studio/app-version/workspace-env/last-version')
}

export function getCategoryVersion(category) {
    return request.get(`/api_v3/studio/app-version/category/${category}/last-version`)
}

export function buildApp(params) {
    return request.post('/api_v3/studio/app-version/build', params)
}

export function deploymentApp(params) {
    return request.post('/api_v3/studio/app-version/deployment', params)
}
export function getFunctionBuildStatus(params) {
    const { appId, appCode, functionId } = params
    return request.get(
        `/api/extended-function/${appId}/code/${appCode}/get-build-function-status`,
        {
            params: {
                functionId,
            },
            isCancel: false,
        }
    )
}

export function getValidateFunctionBuildStatus(params) {
    const { appId, appCode, ...payload } = params
    return request.post(
        `/api/extended-function/${appId}/code/${appCode}/get-batch-build-function-status`,
        payload
    )
}

// data service
export function getDataObjectList() {
    return request.get('/api_v3/studio/objects/modeling/group/list')
}

export function getDataServiceList() {
    return request.get('/api_v3/studio/service/list/group')
}

// data Expression
export function getDataExpressionList(pageId) {
    return request.get(
        `/api_v3/studio/page/${pageId}/expression`
    )
}

export function getDataExpression(pageId, expressionId) {
    return request.get(
        `/api_v3/studio/page/${pageId}/expression/${expressionId}`
    )
}

export function createDataExpression(
    pageId,
    expression
) {
    return request.post(
        `/api_v3/studio/page/${pageId}/expression`,
        expression
    )
}

export function deleteDataExpression(pageId, id) {
    // /api/modeling/app/:appId/workspace/:workspaceId/page/:pageId/page-expression/:id
    return request.delete(
        `/api_v3/studio/page/${pageId}/expression/${id}`
    )
}

export function updateDataExpression(
    pageId,
    expression
) {
    // /api/modeling/app/:appId/workspace/:workspaceId/page/:pageId/page-expression/:id
    const { id: expressionId, ...data } = expression
    return request.put(
        `/api_v3/studio/page/${pageId}/expression/${expressionId}`,
        data
    )
}

export function fetchMetaData(params) {
    return request.post('/api/publish/fetchMetadata', params)
}

export function savePreviewToken(params) {
    return request.post('/api/save-preview-token', params)
}

/**
 *   刷新bff token
 * @param {*} params
 * @returns
 */
export function refreshToken(params) {
    return request.post('/api/refresh-token', params)
}

/**
 * 获取导航页面
 * @returns {Array<String>}
 */
export function getNavPages() {
    return request.get('/api/page/nav')
}

/**
 * 获取页面权限列表
 * @returns {*}
 */
export function getAuthList() {
    return request.get('/api/role-auth/auth')
}

/**
 * 更新权限列表
 * @param params
 * @returns {*}
 */
export function addAuthList(params) {
    return request.post('/api/role-auth/auth', params)
}

export function deleteAuthList(params) {
    return request.delete('/api/role-auth/auth', {
        data: params,
    })
}

/**
 * 获取角色首页
 * @returns {*}
 */
export function getRoleIndexPage() {
    return request.get('/api/role-auth/index-page')
}

export function getCheckErrorList(params) {
    return request.get('/api_v3/studio/model-check', { params })
}

export function getStarMapEnv(params) {
    return request.get('/api_v3/developer/xingtu/env/list', { params })
}

export function getServiceUpdate(params) {
    const { xingTuEnvId, ...others } = params
    return request.get(`/api_v3/studio/star-map/update/count/${params.xingTuEnvId}`, { params: others })
}

/**
 * 关联规则
 */
export function getLinkageList(pageId) {
    return xhttp.get(`/api_v3/studio/page/${pageId}/linkage`)
}
export function getLinkageInfo(params) {
    return xhttp.get(`/api_v3/studio/page/${params.pageId}/linkage/${params.pageLinkageRuleId}`)
}
export function addLinkage(params) {
    return xhttp.post(`/api_v3/studio/page/${params.pageId}/linkage`, params)
}
export function editLinkageName(params) {
    return xhttp.put(`/api_v3/studio/page/${params.pageId}/linkage/${params.pageLinkageRuleId}/name`, params)
}
export function editLinkageInfo(params) {
    return xhttp.put(`/api_v3/studio/page/${params.pageId}/linkage/${params.pageLinkageRuleId}/content`, params)
}
export function deleteLinkage(params) {
    return xhttp.delete(`/api_v3/studio/page/${params.pageId}/linkage/${params.pageLinkageRuleId}`)
}

// 素材库
export function getImageLibraryFolder() {
    return xhttp.get('/api_v3/studio/image-folders')
}
export function getImageLibrary(params) {
    return xhttp.get('/api_v3/studio/images', { params })
}
export function addImageFolder(params) {
    return xhttp.post('/api_v3/studio/image-folders', params)
}
export function addImages(params) {
    return xhttp.post('/api_v3/studio/images/batch', params)
}
export function delImage(imageId) {
    return xhttp.delete(`/api_v3/studio/images/${imageId}`)
}
